import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Rocket, Users, MapPin } from "lucide-react";

const INDUSTRY_LABELS = {
  hospitals: "Hospitals", pharma: "Pharma", diagnostics: "Diagnostics",
  medtech: "MedTech", biotech: "Biotech", digital_health: "Digital Health",
  insurance: "Insurance", health_ai: "Health AI", telemedicine: "Telemedicine",
  wellness: "Wellness", consulting: "Consulting",
};

const STAGE_LABEL = {
  seed: "Seed", series_a: "Series A", series_b: "Series B",
  series_c: "Series C", growth: "Growth", public: "Public",
};

const STAGE_STYLE = {
  seed: "bg-secondary/12 text-secondary border-secondary/30",
  series_a: "bg-primary/8 text-primary border-primary/20",
  series_b: "bg-primary/12 text-primary border-primary/25",
  series_c: "bg-accent/12 text-accent border-accent/25",
  growth: "bg-[#EAB308]/12 text-[#854D0E] border-[#EAB308]/25",
  public: "bg-[#64748B]/12 text-[#334155] border-[#64748B]/25",
};

export default function StartupCard({ startup }) {
  const industry = INDUSTRY_LABELS[startup.industry] || startup.industry;
  return (
    <Link
      to={`/startups/${startup.id}`}
      className="group block rounded-lg border border-border bg-white p-5 hover:border-primary transition-colors"
      data-testid={`startup-card-${startup.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-md bg-primary/8 text-primary flex items-center justify-center shrink-0">
            <Rocket size={18} />
          </div>
          <div>
            <h3 className="font-serif text-xl text-primary leading-tight">{startup.name}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{startup.tagline}</p>
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-semibold rounded-full border px-2 py-0.5 ${STAGE_STYLE[startup.stage] || STAGE_STYLE.seed}`} data-testid={`startup-stage-${startup.id}`}>
          {STAGE_LABEL[startup.stage] || startup.stage}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5" data-testid={`startup-products-${startup.id}`}>
        {(startup.product_lines || []).slice(0, 4).map((p) => (
          <span key={p} className="rounded-full bg-muted text-primary text-[11px] px-2.5 py-0.5">{p}</span>
        ))}
        {(startup.product_lines || []).length > 4 && (
          <span className="rounded-full bg-muted text-muted-foreground text-[11px] px-2.5 py-0.5">
            +{startup.product_lines.length - 4} more
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-lg font-semibold text-primary">₹{startup.funding_raised_inr_cr}Cr</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Raised</div>
        </div>
        <div>
          <div className="text-lg font-semibold text-primary">{startup.founded_year}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Founded</div>
        </div>
        <div>
          <div className="text-lg font-semibold text-primary flex items-center gap-1"><Users size={14} /> {startup.team_size}</div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Team</div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs">
        <span className="inline-flex items-center gap-1 text-muted-foreground"><MapPin size={11} /> {startup.hq_city}</span>
        <span className="rounded-full bg-muted text-primary px-2 py-0.5 font-medium">{industry}</span>
      </div>

      <div className="mt-3 flex items-center justify-end text-xs">
        <span className="text-primary font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
          Read more <ArrowRight size={11} />
        </span>
      </div>
    </Link>
  );
}
